import { useState, useCallback, useEffect, useRef } from "react";
import { useWebSocket } from "../context/WebSocketContext";
import { useAuth } from "../context/AuthContext";

import MessageSender from "../components/MessageSender";

function WhatsAppConnectionInterface() {
  const { user } = useAuth();

  const {
    ws,
    status,
    isConnecting,
    reconnectAttempts,
    messages,
    sendAction,
    manualReconnect,
    clearMessages,
  } = useWebSocket();

  // Estados específicos do WhatsApp
  const [connectionStatus, setConnectionStatus] = useState({
    hasService: false,
    isConnected: false,
    isInitializing: false,
    hasAutomation: false,
    connectedClients: 0,
    uptime: 0,
  });

  const [qrCode, setQrCode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // Guarda a tentativa automática para ela acontecer uma vez por visita à tela,
  // e não a cada atualização de status vinda do servidor.
  const autoStartedRef = useRef(false);

  // Estados do formulário de mensagem
  const [messageToSend, setMessageToSend] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  useEffect(() => {
    sendAction("get_status");
  }, [sendAction]);

  // Processar mensagens específicas do WhatsApp
  // Handler para mensagens específicas do WhatsApp
  const handleWhatsAppMessage = useCallback((data) => {
    const handlers = {
      connection_established: () => {
        if (data.serverStatus) setConnectionStatus(data.serverStatus);
      },

      initialization_started: () => setIsLoading(true),

      qr_code_generated: () => {
        setQrCode(data.qrDataUrl || `data:image/png;base64,${data.qr}`);
        setIsLoading(false);
      },

      qr_code_refreshing: () => {
        setQrCode(null);
        setIsLoading(true);
      },

      session_reset_started: () => {
        setQrCode(null);
        setIsLoading(true);
      },

      whatsapp_status_change: () => {
        if (data.connectionStatus) setConnectionStatus(data.connectionStatus);
        if (["ready", "connected"].includes(data.status)) {
          setQrCode(null);
          setIsLoading(false);
        }
      },

      status_response: () => {
        if (data.status) setConnectionStatus(data.status);
      },

      whatsapp_disconnected: () => {
        setQrCode(null);
        setIsLoading(false);
        setConnectionStatus((prev) => ({
          ...prev,
          isConnected: false,
          hasService: false,
          isInitializing: false,
          hasAutomation: false,
        }));
      },

      restart_single_started: () => {
        setIsLoading(true);
        setConnectionStatus((prev) => ({
          ...prev,
          isConnected: false,
          hasService: false,
          isInitializing: false,
          hasAutomation: false,
        }));
      },

      automation_engine_ready: () => {
        setConnectionStatus((prev) => ({
          ...prev,
          hasAutomation: true,
        }));
      },

      initialization_failed: () => setIsLoading(false),
      qr_code_error: () => setIsLoading(false),
      disconnection_error: () => setIsLoading(false),
      message_sent_error: () => setIsLoading(false),
    };

    // Executa o handler correspondente, se existir
    handlers[data.type]?.();
  }, []);

  useEffect(() => {
    if (messages.length === 0) return;

    const latestMessage = messages[0];
    handleWhatsAppMessage(latestMessage);
  }, [handleWhatsAppMessage, messages]);

  // Sem conexão e sem QR na tela, a inicialização sobe sozinha: quem abre esta
  // página desconectado quer o código, não um botão a mais para clicar.
  useEffect(() => {
    if (autoStartedRef.current) return;
    if (connectionStatus.isConnected || connectionStatus.isInitializing) return;
    if (qrCode || isLoading) return;

    autoStartedRef.current = true;
    setIsLoading(true);
    sendAction("initialize_whatsapp");
  }, [connectionStatus, qrCode, isLoading, sendAction]);

  // Reconectou: libera a inicialização automática para a próxima queda.
  useEffect(() => {
    if (connectionStatus.isConnected) autoStartedRef.current = false;
  }, [connectionStatus.isConnected]);

  const handleSendMessage = () => {
    const cleanPhone = phoneNumber.trim();
    const cleanMessage = messageToSend.trim();

    if (!cleanPhone || !cleanMessage) {
      alert("Por favor, preencha o número e a mensagem");
      return;
    }

    if (!connectionStatus.isConnected) {
      alert("WhatsApp não está conectado");
      return;
    }

    sendAction("send_message", {
      userId: user.id,
      username: user.name,
      phoneNumber: cleanPhone,
      message: cleanMessage,
    });

    setMessageToSend("");
  };

  // Ações do WhatsApp
  const initialize = () => {
    setIsLoading(true);
    sendAction("initialize_whatsapp");
  };

  const disconnect = () => {
    sendAction("disconnect_whatsapp");
  };

  const restart = () => {
    setIsLoading(true);
    sendAction("restart_whatsapp");
  };

  const restartSingle = () => {
    setIsLoading(true);
    sendAction("restart_single");
  };

  const refreshStatus = () => {
    sendAction("get_status");
  };

  const refreshQrCode = () => {
    setQrCode(null);
    setIsLoading(true);
    sendAction("refresh_qr");
  };

  const resetSession = () => {
    const confirmed = window.confirm(
      "Apagar a sessão salva e começar do zero?\n\n"
      + "Use quando o QR Code não aparece e o log mostra 'auth timeout': "
      + "significa que a sessão gravada está inválida e precisa ser descartada.\n\n"
      + "O número precisará ser pareado novamente pelo celular.",
    );

    if (!confirmed) return;

    setQrCode(null);
    setIsLoading(true);
    sendAction("reset_session");
  };

  // Funções utilitárias
  const getStatusColor = () => {
    if (connectionStatus.isConnected)
      return "text-green-600 dark:text-green-400";
    if (connectionStatus.isInitializing || isLoading)
      return "text-yellow-600 dark:text-yellow-400";
    if (connectionStatus.hasService) return "text-blue-600 dark:text-blue-400";
    return "text-red-600 dark:text-red-400";
  };

  const getStatusIcon = () => {
    if (connectionStatus.isConnected) return "🟢";
    if (connectionStatus.isInitializing || isLoading) return "🟡";
    if (connectionStatus.hasService) return "🔵";
    return "🔴";
  };

  const formatUptime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const getMessageTypeColor = (type) => {
    if (type.includes("error") || type.includes("failed")) {
      return "bg-red-50 border-red-400 dark:border-red-600 dark:bg-red-900";
    } else if (type.includes("qr_code")) {
      return "bg-blue-50 border-blue-400 dark:border-blue-600 dark:bg-blue-900";
    } else if (
      type.includes("ready") ||
      type.includes("connected") ||
      type.includes("success")
    ) {
      return "bg-green-50 border-green-400 dark:border-green-600 dark:bg-green-900";
    } else if (type.includes("message")) {
      return "bg-purple-50 border-purple-400 dark:border-purple-600 dark:bg-purple-900";
    } else if (type.includes("automation")) {
      return "bg-indigo-50 border-indigo-400 dark:border-indigo-600 dark:bg-indigo-900";
    } else {
      return "bg-gray-50 border-gray-400 dark:border-gray-600 dark:bg-gray-900";
    }
  };

  const getMessageTypeIcon = (type) => {
    const icons = {
      connection_established: "🔗",
      initialization_started: "🚀",
      qr_code_generated: "📱",
      whatsapp_status_change: "📊",
      status_response: "📋",
      whatsapp_disconnected: "📴",
      automation_engine_ready: "🤖",
      whatsapp_message_sent: "📤",
      whatsapp_message_received: "📥",
      initialization_failed: "❌",
      qr_code_error: "⚠️",
      disconnection_error: "💥",
      message_sent_error: "🚫",
      server_shutting_down: "🛑",
      warning: "⚠️",
      info: "ℹ️",
      restart_initiated: "🔄",
    };
    return icons[type] || "📄";
  };

  const formatMessage = (msg) => {
    const copy = { ...msg };
    delete copy.id;
    delete copy.receivedAt;
    return JSON.stringify(copy, null, 2);
  };

  return (
    <div className="flex min-h-screen w-full space-x-4 bg-gray-50 p-4 text-gray-900 transition-colors duration-300 dark:bg-gray-800 dark:text-gray-100 md:p-6">
      <div className="mx-auto max-w-6xl flex-1 space-y-6">
        {/* Header */}
        <div className="rounded-xl bg-white p-6 shadow-lg transition-colors duration-300 dark:bg-gray-900">
          <h1 className="mb-3 text-3xl font-bold text-gray-800 dark:text-gray-100">
            📱 WhatsApp CRM Monitor
          </h1>

          <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
            <div className={`font-semibold ${getStatusColor()}`}>
              {getStatusIcon()} Status: {status}
            </div>
            <div className="text-gray-600 dark:text-gray-300">
              👥 Clientes WebSocket: {connectionStatus.connectedClients}
            </div>
            <div className="text-gray-600 dark:text-gray-300">
              ⏱️ Uptime: {formatUptime(connectionStatus.uptime || 0)}
            </div>
          </div>

          {/* Controles de conexão */}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={refreshStatus}
              disabled={ws?.readyState !== WebSocket.OPEN}
              className="rounded-lg bg-blue-100 px-3 py-1 text-sm text-blue-700 transition-colors hover:bg-blue-200 disabled:bg-gray-100 disabled:text-gray-400 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800 dark:disabled:bg-gray-700"
            >
              🔄 Atualizar Status
            </button>

            <button
              onClick={manualReconnect}
              disabled={isConnecting}
              className="rounded-lg bg-purple-100 px-3 py-1 text-sm text-purple-700 transition-colors hover:bg-purple-200 disabled:bg-gray-100 disabled:text-gray-400 dark:bg-purple-900 dark:text-purple-200 dark:hover:bg-purple-800 dark:disabled:bg-gray-700"
            >
              {isConnecting ? "🔄 Conectando..." : "🔌 Reconectar"}
            </button>

            {reconnectAttempts > 0 && (
              <span className="rounded-lg bg-yellow-100 px-3 py-1 text-sm text-yellow-700 dark:bg-yellow-900 dark:text-yellow-200">
                🔄 Tentativa automática: {reconnectAttempts}
              </span>
            )}
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {/* Serviço WhatsApp */}
          <div
            className={`rounded-xl bg-white p-6 text-center shadow-lg transition-all duration-300 dark:bg-gray-900 ${
              connectionStatus.hasService
                ? "ring-2 ring-green-200 dark:ring-green-700"
                : ""
            }`}
          >
            <div className="mb-2 text-3xl">
              {connectionStatus.hasService ? "✅" : "❌"}
            </div>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              Serviço WhatsApp
            </div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {connectionStatus.hasService ? "Ativo" : "Inativo"}
            </div>
          </div>

          {/* Conexão */}
          <div
            className={`rounded-xl bg-white p-6 text-center shadow-lg transition-all duration-300 dark:bg-gray-900 ${
              connectionStatus.isConnected
                ? "ring-2 ring-green-200 dark:ring-green-700"
                : ""
            }`}
          >
            <div className="mb-2 text-3xl">
              {connectionStatus.isConnected
                ? "🟢"
                : connectionStatus.isInitializing
                  ? "🟡"
                  : "🔴"}
            </div>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              Conexão
            </div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {connectionStatus.isConnected
                ? "Conectado"
                : connectionStatus.isInitializing
                  ? "Conectando"
                  : "Desconectado"}
            </div>
          </div>

          {/* Automação */}
          <div
            className={`rounded-xl bg-white p-6 text-center shadow-lg transition-all duration-300 dark:bg-gray-900 ${
              connectionStatus.hasAutomation
                ? "ring-2 ring-purple-200 dark:ring-purple-700"
                : ""
            }`}
          >
            <div className="mb-2 text-3xl">
              {connectionStatus.hasAutomation ? "🤖" : "💤"}
            </div>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              Automação
            </div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {connectionStatus.hasAutomation ? "Ativa" : "Inativa"}
            </div>
          </div>

          {/* WebSocket */}
          <div
            className={`rounded-xl bg-white p-6 text-center shadow-lg transition-colors duration-300 dark:bg-gray-900 ${
              ws?.readyState === WebSocket.OPEN
                ? "ring-2 ring-purple-200 dark:ring-purple-700"
                : ""
            }`}
          >
            <div className="mb-2 text-3xl">
              {ws?.readyState === WebSocket.OPEN ? "🔗" : "🔌"}
            </div>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              WebSocket
            </div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {ws?.readyState === WebSocket.OPEN
                ? "Conectado"
                : isConnecting
                  ? "Conectando"
                  : "Desconectado"}
            </div>
          </div>
        </div>

        {/* Controles Principais */}
        <div className="rounded-xl bg-white p-6 shadow-lg transition-colors duration-300 dark:bg-gray-900">
          <h2 className="mb-4 text-xl font-semibold text-gray-800 dark:text-gray-100">
            🎮 Controles do WhatsApp
          </h2>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Iniciar WhatsApp */}
            <button
              onClick={initialize}
              disabled={
                connectionStatus.isConnected ||
                isLoading ||
                ws?.readyState !== WebSocket.OPEN
              }
              className="rounded-lg bg-green-500 px-6 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:bg-green-600 hover:shadow-xl disabled:cursor-not-allowed disabled:bg-gray-400 dark:disabled:bg-gray-600"
            >
              {isLoading ? "🔄 Inicializando..." : "🚀 Iniciar WhatsApp"}
            </button>

            {/* Reiniciar Único */}
            <button
              onClick={restartSingle}
              disabled={
                !connectionStatus.hasService ||
                ws?.readyState !== WebSocket.OPEN
              }
              className="rounded-lg bg-blue-500 px-6 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:bg-blue-600 hover:shadow-xl disabled:cursor-not-allowed disabled:bg-gray-400 dark:disabled:bg-gray-600"
            >
              🔄 Reiniciar Único
            </button>

            {/* Reiniciar */}
            <button
              onClick={restart}
              disabled={
                !connectionStatus.hasService ||
                isLoading ||
                ws?.readyState !== WebSocket.OPEN
              }
              className="rounded-lg bg-yellow-500 px-6 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:bg-yellow-600 hover:shadow-xl disabled:cursor-not-allowed disabled:bg-gray-400 dark:disabled:bg-gray-600"
            >
              🔄 Reiniciar
            </button>

            {/* Desconectar */}
            <button
              onClick={disconnect}
              disabled={
                !connectionStatus.hasService ||
                ws?.readyState !== WebSocket.OPEN
              }
              className="rounded-lg bg-red-500 px-6 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:bg-red-600 hover:shadow-xl disabled:cursor-not-allowed disabled:bg-gray-400 dark:disabled:bg-gray-600"
            >
              🛑 Desconectar
            </button>
          </div>
        </div>

        {/* QR Code — presente sempre que não houver conexão ativa */}
        {!connectionStatus.isConnected && (
          <div className="animate-fade-in rounded-xl bg-white p-6 text-center shadow-lg transition-colors duration-300 dark:bg-gray-900">
            <h2 className="mb-4 text-xl font-semibold text-gray-800 dark:text-gray-100">
              📱 Conectar o WhatsApp
            </h2>

            <div className="inline-block rounded-xl bg-gray-50 p-4 shadow-inner transition-colors duration-300 dark:bg-gray-800">
              {qrCode ? (
                <img
                  src={qrCode}
                  alt="QR Code WhatsApp"
                  className="mx-auto max-w-xs rounded-lg shadow-lg"
                />
              ) : (
                <div className="flex h-64 w-64 flex-col items-center justify-center gap-3 text-gray-500 dark:text-gray-400">
                  <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-green-500" />
                  <p className="text-sm">Gerando o QR Code...</p>
                </div>
              )}
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <button
                onClick={refreshQrCode}
                disabled={isLoading}
                className="rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                🔄 Gerar novo QR Code
              </button>

              <button
                onClick={refreshStatus}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Verificar status
              </button>

              {user?.role === "ADMIN" && (
                <button
                  onClick={resetSession}
                  disabled={isLoading}
                  className="rounded-lg border border-red-300 px-4 py-2 text-sm text-red-700 transition-colors hover:bg-red-50 disabled:opacity-60 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                  title="Apaga a sessão salva e força um pareamento novo"
                >
                  Apagar sessão e recomeçar
                </button>
              )}
            </div>

            <p className="mx-auto mt-2 max-w-md text-xs text-gray-500 dark:text-gray-400">
              O código expira em poucos minutos. Se ele venceu antes de você
              escanear, gere um novo. Se o QR não aparece de jeito nenhum e o log
              mostra <span className="font-mono">auth timeout</span>, a sessão
              salva está inválida — use "Apagar sessão e recomeçar".
            </p>

            <div className="mx-auto mt-4 max-w-md text-gray-600 transition-colors duration-300 dark:text-gray-300">
              <p className="mb-2 font-medium">Instruções:</p>
              <ol className="list-inside list-decimal space-y-1 text-left text-sm text-gray-600 dark:text-gray-400">
                <li>Abra o WhatsApp no seu celular</li>
                <li>
                  Toque em{" "}
                  <span className="font-semibold">"Mais opções" (⋮)</span> →{" "}
                  <span className="font-semibold">"Dispositivos conectados"</span>
                </li>
                <li>Escaneie este código QR</li>
                <li>Aguarde a conexão ser estabelecida</li>
              </ol>
            </div>
          </div>
        )}

        {/* Envio de Mensagens */}
        {connectionStatus.isConnected && (
          <MessageSender
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            messageToSend={messageToSend}
            setMessageToSend={setMessageToSend}
            onSend={handleSendMessage}
            wsReady={ws?.readyState === WebSocket.OPEN}
          />
        )}

        {/* Loading Indicator */}
        {isLoading && (
          <div className="rounded-xl bg-white p-6 text-center shadow-lg transition-colors duration-300 dark:bg-gray-900">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-green-500"></div>
            <p className="font-medium text-gray-600 dark:text-gray-300">
              Processando operação...
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Aguarde enquanto o WhatsApp é configurado
            </p>
          </div>
        )}
      </div>

      {/* Log de Mensagens */}
      <div className="flex flex-1 flex-col rounded-xl bg-white p-6 shadow-lg transition-colors duration-300 dark:bg-gray-900">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
            📋 Log de Eventos
          </h2>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {messages.length} evento(s)
            </span>
            <button
              onClick={clearMessages}
              className="text-sm text-red-600 underline transition-colors hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            >
              🗑️ Limpar Log
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto rounded-xl border bg-gray-50 p-4 transition-colors duration-300 dark:border-gray-700 dark:bg-gray-800">
          {messages.length === 0 ? (
            <div className="py-12 text-center text-gray-500 dark:text-gray-400">
              <div className="mb-4 text-6xl">📭</div>
              <p className="text-lg font-medium">Nenhum evento registrado</p>
              <p className="mt-2 text-sm">
                Os eventos do servidor aparecerão aqui em tempo real
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`rounded-lg p-4 transition-all hover:shadow-md dark:bg-gray-900 dark:hover:shadow-lg ${getMessageTypeColor(
                    msg.type,
                  )}`}
                >
                  <div className="mb-2 flex items-start justify-between">
                    <span className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-200">
                      {getMessageTypeIcon(msg.type)}
                      <span className="ml-2">
                        {msg.type.replace(/_/g, " ")}
                      </span>
                    </span>
                    <span className="rounded bg-white px-2 py-1 text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                      {msg.receivedAt}
                    </span>
                  </div>

                  {/* Mensagem principal */}
                  {msg.message && (
                    <div className="mb-2 text-sm font-medium text-gray-800 dark:text-gray-100">
                      {msg.message.from || msg.message}
                    </div>
                  )}

                  {/* Instruções do QR Code */}
                  {msg.instructions && (
                    <div className="mb-2 rounded bg-white p-2 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      <strong>Instruções:</strong>
                      <ul className="ml-4 mt-1 list-disc">
                        {msg.instructions.map((instruction, idx) => (
                          <li key={idx}>{instruction}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Status de conexão */}
                  {msg.connectionStatus && (
                    <div className="mb-2 rounded bg-white p-2 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      🔗 WhatsApp:{" "}
                      {msg.connectionStatus.isConnected
                        ? "✅ Conectado"
                        : "❌ Desconectado"}{" "}
                      • Serviço:{" "}
                      {msg.connectionStatus.hasService
                        ? "✅ Ativo"
                        : "❌ Inativo"}{" "}
                      • Automação:{" "}
                      {msg.connectionStatus.hasAutomation
                        ? "🤖 Ativa"
                        : "💤 Inativa"}
                    </div>
                  )}

                  {/* Dados técnicos (colapsível) */}
                  <details className="text-xs">
                    <summary className="cursor-pointer font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                      🔍 Ver dados técnicos
                    </summary>
                    <pre className="mt-2 overflow-x-auto rounded border bg-white p-3 text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
                      {formatMessage(msg)}
                    </pre>
                  </details>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default WhatsAppConnectionInterface;
